import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Hook that connects to /api/ws/notifications and shows toasts for incoming notifications.
 * Auth: sends {"auth": token} as first message after WS connects.
 * @param {string|null} token - user auth token
 */
export function useNotificationWS(token) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  useEffect(() => {
    if (!token) return;

    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = baseUrl.replace(/^http/, 'ws') + '/api/ws/notifications';

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          // Send auth message
          ws.send(JSON.stringify({ auth: token }));
        };

        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);

            if (data.error === 'unauthorized') {
              ws.close();
              return;
            }

            if (data.event === 'notification' && data.notification) {
              const n = data.notification;
              if (n.type === 'error') {
                toast.error(n.title, { description: n.message, duration: 6000 });
              } else if (n.type === 'warning') {
                toast.warning(n.title, { description: n.message, duration: 5000 });
              } else if (n.type === 'success') {
                toast.success(n.title, { description: n.message, duration: 4000 });
              } else {
                toast(n.title, { description: n.message, duration: 4000 });
              }
            }
          } catch {}
        };

        ws.onclose = () => {
          wsRef.current = null;
          reconnectTimer.current = setTimeout(connect, 5000);
        };

        ws.onerror = () => ws.close();
      } catch {}
    };

    connect();

    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [token]);
}
